import {
  generateReport, reportStem,
  type RapportKilde, type RapportPost, type RapportSpoersmaal,
  type RapportStiltEllers, type RapportSvar, type ReportNaming,
} from "@infoflyt/shared/rapport";
import { timecodeAt, timecodeSpec, type FramerateName } from "@infoflyt/shared/timecode";
import { finnesAlt } from "@infoflyt/shared/lager";
import { supabase } from "./supabase";
import type { Oekt } from "./session";
import * as nettlogg from "./nettlogg";
import * as deltakere from "./deltakere";
import * as su from "./su";
import * as logo from "./logo";

/**
 * Avslutt dagen, fra nettflata.
 *
 * ========  HVORFOR DEN MAATTE FINNES HER OGSAA  ========
 *
 * Brukeren 02.11.2026: «Man maa ha en maate aa avslutte dagen paa i
 * nettflata ogsaa, slik at live loggen og synken blir til en rapport.»
 *
 * Fram til naa kunne bare appen levere. Det holdt saa lenge nettflata var
 * lesing — men liveloggen og synken ER logging, og en logg uten rapport er
 * en logg ingen leser: rapporten er det redaktoeren faar.
 *
 * ========  SAMME DOKUMENT, SAMME BYGGER  ========
 *
 * `generateReport` ligger i `packages/shared/rapport` fra samme dato,
 * nettopp for dette. To byggere ville gitt to dokumenter, og redaktoeren
 * skal ikke kunne se hvilken flate rapporten ble laget paa.
 *
 * ========  OG SAMME VEI INN I BASEN  ========
 *
 * `lever_rapport`. Serveren setter forfatteren selv og avgjoer versjonen —
 * klienten paastaar ingenting. Se migrasjon 20261102200000.
 *
 * ÉN RAPPORT PER OEKT. `report.session_id` er én fremmednoekkel, saa
 * liveloggen og synken maa skrive i samme oekt for aa kunne bli én rapport.
 * Det er grunnen til at `synk.Hvem` fikk `teamId` 02.11.2026.
 */

export const SKIFT = ["Dag", "Midt", "Kveld", "Natt"] as const;
export type Skift = (typeof SKIFT)[number];

/**
 * Skiftet, gjettet av klokka.
 *
 * En gjetning og ikke en sannhet: feltet kan endres. Men den som avslutter
 * klokka tre om natta skal ikke maatte velge «Natt» selv.
 */
export function skiftNaa(naa: Date = new Date()): Skift {
  const t = naa.getHours();
  if (t >= 5 && t < 12) return "Dag";
  if (t >= 12 && t < 17) return "Midt";
  if (t >= 17 && t < 23) return "Kveld";
  return "Natt";
}

/** Dagens dato i Oslo, ISO. Rapporten foerer datoen dagen hoerer til. */
export function datoIdag(naa: Date = new Date()): string {
  return naa.toLocaleDateString("sv-SE", { timeZone: "Europe/Oslo" });
}

export interface Levert {
  readonly versjon: number;
  /** Naar dagen ble avsluttet. Streken i loggen staar her. */
  readonly lukketMs: number | null;
}

/**
 * Rapportene som alt er levert for oekta, eldste foerst.
 *
 * Liveloggen tegner en strek av dem: «en levert rapport setter en strek i
 * loggen» — rapporten er et oeyeblikksbilde, og det som skrives etterpaa er
 * ikke med i den. I appen leses streken av `audit_log`; den er stengt for
 * klientene, saa her er rapportraden kilden. Det er den samme hendelsen.
 */
export async function hentLevert(oektId: string | null): Promise<readonly Levert[]> {
  if (supabase === null || oektId === null) return [];
  const { data, error } = await supabase
    .from("report")
    .select("version, closed_at")
    .eq("session_id", oektId)
    .order("version", { ascending: true });
  if (error !== null || data === null) return [];
  return (data as unknown as Record<string, unknown>[]).map((r) => ({
    versjon: Number(r["version"] ?? 1),
    lukketMs: r["closed_at"] == null ? null : Date.parse(String(r["closed_at"])),
  }));
}

export interface Leveranse {
  readonly oekt: Oekt;
  readonly oektId: string;
  readonly anker: nettlogg.Anker;
  readonly spec: ReturnType<typeof timecodeSpec>;
  readonly poster: readonly nettlogg.Loggpost[];
  readonly oppsummering: string;
  readonly skift: string;
  readonly datoIso: string;
}

export type Utfall =
  | { readonly ok: true; readonly versjon: number; readonly filnavn: string }
  | { readonly ok: false; readonly feil: string };

/** Logoen som data-URL, eller null. jsPDF tegner ikke SVG — se pdf.ts. */
async function logoDataUrl(sti: string | null | undefined): Promise<string | null> {
  if (sti === null || sti === undefined || sti === "") return null;
  if (/\.svg$/i.test(sti)) return null;
  try {
    const lenke = await logo.lenke(sti);
    if (lenke === null) return null;
    const svar = await fetch(lenke);
    if (!svar.ok) return null;
    const blob = await svar.blob();
    return await new Promise<string | null>((ferdig) => {
      const leser = new FileReader();
      leser.onload = () => ferdig(typeof leser.result === "string" ? leser.result : null);
      leser.onerror = () => ferdig(null);
      leser.readAsDataURL(blob);
    });
  } catch {
    /* Rapporten skal ut uansett. Et brevhode er ikke verdt en levering. */
    return null;
  }
}

/**
 * Opphavsposten, regnet om til det rapporten skal vise.
 *
 * TIDSKODEN REGNES HER, av postens EGET anker — ikke av dagens. En post fra
 * opptaksdag 6 hører til dag 6 sitt anker, og dagens ville gitt et tall som
 * ikke finnes noe sted i materialet.
 *
 * Rapportversjonen er den HØYESTE som er levert for økta: er det levert
 * flere, er den siste den klipperen har. Samme regning som appen gjør i
 * `state/su.ts` — og den må være den samme, ellers peker de to rapportene
 * på hver sin ting.
 */
function tilKilde(rad: Record<string, unknown>): RapportKilde | null {
  const id = rad["source_entry_id"];
  const k = rad["kilde"] as Record<string, unknown> | null | undefined;
  if (id === null || id === undefined || k === null || k === undefined) return null;

  const a = k["anker"] as Record<string, unknown> | null | undefined;
  const oekt = k["oekt"] as Record<string, unknown> | null | undefined;
  if (a === null || a === undefined) return null;

  const rate = String(a["framerate"]) as FramerateName;
  const dropFrame = Boolean(a["drop_frame"]);
  const spec = timecodeSpec(rate, dropFrame);
  const t = timecodeAt(
    {
      tcFrames: Number(a["tc_frames"]),
      monotonicMs: Number(a["monotonic_ms"]),
      bootId: String(a["boot_id"]),
      wallUtcMs: Date.parse(String(a["wall_utc"])),
      framerate: rate,
      dropFrame,
      source: "manual",
    },
    {
      monotonicMs: Number(k["monotonic_ms"]),
      bootId: String(k["boot_id"]),
      wallUtcMs: Date.parse(String(k["wall_utc"])),
    },
    spec,
  );

  const rapporter = (oekt?.["report"] ?? []) as readonly Record<string, unknown>[];
  const versjoner = rapporter.map((r) => Number(r["version"] ?? 0));

  return {
    entryId: String(id),
    tcFrames: t.frames,
    framerate: rate,
    dropFrame,
    opptaksdag: String(oekt?.["shoot_day"] ?? ""),
    tekst: String(k["text"] ?? ""),
    rapportversjon: versjoner.length === 0 ? null : Math.max(...versjoner),
  };
}

/** Neste ledige versjon, gjettet. Serveren avgjoer — se `lever_rapport`. */
async function nesteVersjon(oektId: string): Promise<number> {
  if (supabase === null) return 1;
  const { data } = await supabase
    .from("report")
    .select("version")
    .eq("session_id", oektId)
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();
  const forrige = (data as { version?: number } | null)?.version;
  return typeof forrige === "number" ? forrige + 1 : 1;
}

export async function lever(l: Leveranse): Promise<Utfall> {
  if (supabase === null) return { ok: false, feil: "Ingen forbindelse til basen." };

  const produksjonId = l.oekt.production.id;

  /*
   * SJEKKLISTA OG SVARENE, hentet HER.
   *
   * Rapporten viser hva som ble spurt om OG hva som ikke ble det — det
   * ustilte er halve verdien for redaktoeren. Et svar teller bare naar
   * synkposten lever, og den proeven gjoer `generateReport` selv mot
   * postene: derfor sendes alt inn uten aa vaere delt opp paa forhaand.
   */
  const [folk, liste, svarsvar, kildesvar, nummer] = await Promise.all([
    deltakere.hent(produksjonId),
    su.hent(produksjonId),
    /*
     * SVARENE, MED HVEM OG HVILKEN DAG.
     *
     * Rapporten skiller tre tilstander: stilt i denne loggen, stilt av en
     * annen foer rapporten gikk, og ikke stilt. Brukeren 03.11.2026: «hvis
     * det er noen andre som har synket … saa kommer det opp at spoersmaalet
     * er stilt av (for eksempel Lars)».
     *
     * `!inner` med `deleted_at is null` holder invarianten: et svar teller
     * bare naar synkposten lever.
     */
    supabase
      .from("su_answer")
      .select("entry_id, question_id, participant_id, answer,"
        + " post:log_entry!inner(deleted_at,"
        + " forfatter:production_member!log_entry_author_id_fkey(display_name),"
        + " oekt:log_session(shoot_day))")
      .eq("production_id", produksjonId)
      .is("post.deleted_at", null),
    /*
     * HVOR SPOERSMAALET KOM FRA.
     *
     * `su.hent` henter ikke koblingen — den trengs bare i rapporten, og en
     * flate som lister spoersmaal skal ikke betale for fem innleirede
     * tabeller. Her er den alt som betyr noe: «synk-rapporten skal
     * referere til opptaks-rapporten som spoersmaalet hoerer til».
     *
     * Tidskoden regnes av opphavspostens EGET anker, ikke dagens — en post
     * fra dag 6 hoerer til dag 6 sitt anker. Samme regning som appen gjoer
     * i state/su.ts.
     */
    supabase
      .from("su_question")
      .select("id, source_entry_id,"
        + " kilde:log_entry!su_question_source_entry_id_fkey("
        + "text, monotonic_ms, boot_id, wall_utc,"
        + " oekt:log_session(shoot_day, report(version)),"
        + " anker:tc_anchor(tc_frames, framerate, drop_frame, monotonic_ms,"
        + " boot_id, wall_utc))")
      .eq("production_id", produksjonId)
      .not("source_entry_id", "is", null),
    nesteVersjon(l.oektId),
  ]);

  const levende = new Set(l.poster.map((p) => p.id));
  const svar: RapportSvar[] = ((svarsvar.data ?? []) as unknown as Record<string, unknown>[])
    .map((r) => ({
      entryId: String(r["entry_id"] ?? ""),
      spoersmaalId: String(r["question_id"] ?? ""),
      deltakerId: String(r["participant_id"] ?? ""),
      svar: String(r["answer"] ?? ""),
    }))
    /* Bare det som hoerer til DENNE dagens poster. Et svar paa dag 6 skal
       ikke staa som stilt i rapporten for dag 7. */
    .filter((s) => levende.has(s.entryId));

  /* Koblingen til loggposten, etter spoersmaals-id. */
  const kilder = new Map<string, RapportKilde>();
  for (const rad of (kildesvar.data ?? []) as unknown as Record<string, unknown>[]) {
    const kilde = tilKilde(rad);
    if (kilde !== null) kilder.set(String(rad["id"]), kilde);
  }

  const spoersmaal: RapportSpoersmaal[] = liste.spoersmaal.map((q) => ({
    id: q.id,
    deltakerId: q.deltakerId,
    tekst: q.tekst,
    skrevetMs: q.skrevetMs,
    opptaksdag: q.opptaksdag,
    kilde: kilder.get(q.id) ?? null,
  }));

  /*
   * Stilt et ANNET sted: et svar hvis synkpost ikke er i denne loggen.
   *
   * Et spoersmaal som er stilt er stilt for alle — men rapporten er én
   * loggs dokument, og uten dette sto alt som ble stilt av en annen som
   * «ikke stilt».
   */
  const stiltEllers: RapportStiltEllers[] =
    ((svarsvar.data ?? []) as unknown as Record<string, unknown>[])
      .filter((r) => !levende.has(String(r["entry_id"] ?? "")))
      .map((r) => {
        const post = (r["post"] ?? {}) as Record<string, unknown>;
        const hvem = (post["forfatter"] ?? {}) as { display_name?: string };
        const oekt = (post["oekt"] ?? {}) as { shoot_day?: string };
        return {
          spoersmaalId: String(r["question_id"] ?? ""),
          deltakerId: String(r["participant_id"] ?? ""),
          hvem: String(hvem.display_name ?? ""),
          opptaksdag: String(oekt.shoot_day ?? ""),
        };
      });

  /*
   * Postene, oversatt til det rapporten trenger.
   *
   * `deletedAt: null` — `hentPoster` filtrerer gravsteinene bort alt, og
   * lista her er den SAMME flata har vist. Rapporten er et oeyeblikksbilde
   * av det som staar paa skjermen.
   */
  const poster: RapportPost[] = l.poster.map((p) => ({
    id: p.id,
    label: p.label,
    text: p.tekst,
    monotonicMs: p.monotonicMs,
    bootId: p.bootId,
    wallUtcMs: p.wallUtcMs,
    tcState: p.tcUsikker ? "usikker" : "ok",
    deletedAt: null,
  }));

  const naming: ReportNaming = {
    production: l.oekt.production.name,
    shootDay: l.oekt.shootDay,
    dateIso: l.datoIso,
    unit: l.oekt.member.unitName ?? "",
    shift: l.skift,
    initials: l.oekt.member.initials,
    version: nummer,
  };

  const rapport = generateReport({
    production: { name: l.oekt.production.name },
    member: { displayName: l.oekt.member.displayName },
    anchor: {
      tcFrames: l.anker.tcFrames,
      monotonicMs: l.anker.monotonicMs,
      bootId: l.anker.bootId,
      wallUtcMs: l.anker.wallUtcMs,
      framerate: l.oekt.production.framerate,
      dropFrame: l.oekt.production.dropFrame,
      source: "manual",
    },
    spec: l.spec,
    entries: poster,
    summary: l.oppsummering,
    naming,
    /* Naar tidskoden ble satt, i lokal tid. Ankerets egen tid — paa nett er
       det jammens, slik toppstripa ogsaa leser den. */
    anchorSetAtLocal: new Date(l.anker.wallUtcMs)
      .toLocaleTimeString("nb-NO", { timeZone: "Europe/Oslo", hour: "2-digit", minute: "2-digit" }),
    logoDataUrl: await logoDataUrl(l.oekt.production.logoSti),
    deltakere: folk.map((d) => ({ id: d.id, navn: d.navn, forkortelse: d.forkortelse })),
    spoersmaal,
    svar,
    stiltEllers,
  });

  /*
   * `upsert: false`. EN LEVERT RAPPORT OVERSKRIVES ALDRI.
   *
   * Stien baerer versjonen, saa en ny versjon legger seg ved siden av den
   * gamle. Moeter vi vaar egen forrige opplasting, er den levert — svaret
   * kan ha gaatt tapt paa vei tilbake uten at fila gjorde det. Samme regel
   * som appen, og den er haandhevet i policyen: se 20261029130000.
   *
   * RAA BYTES, IKKE BLOB: `upload` med en Blob bygger en FormData med et
   * navnloest felt, og paa iOS kommer den delen ikke med. Flata brukes fra
   * en iPad.
   */
  const sti = `${produksjonId}/${l.oektId}/v${nummer}.pdf`;
  const { error: filfeil } = await supabase.storage
    .from("rapporter")
    .upload(sti, await rapport.blob.arrayBuffer(), {
      contentType: "application/pdf", upsert: false,
    });
  if (filfeil !== null && !finnesAlt(filfeil)) {
    return { ok: false, feil: `Fikk ikke lastet opp PDF-en: ${filfeil.message}` };
  }

  const lukket = new Date().toISOString();
  const { data: versjonen, error } = await supabase.rpc("lever_rapport", {
    p_id: crypto.randomUUID(),
    p_oekt: l.oektId,
    p_produksjon: produksjonId,
    /* Blokkmodellen fra skjemaet. I v1 finnes bare `paragraph`. */
    p_blocks: l.oppsummering === ""
      ? [] : [{ type: "paragraph", text: l.oppsummering }],
    p_meta: { ...naming },
    p_pdf_sti: sti,
    p_lukket: lukket,
  });
  if (error !== null) return { ok: false, feil: error.message };

  const versjon = typeof versjonen === "number" ? versjonen : nummer;

  /*
   * ========  OG DAGEN LUKKES  ========
   *
   * `status = 'closed'` er det redaktoerens loggflate leser for aa vise at
   * dagen er avsluttet. Den STOPPER ingenting: loggen kan skrives videre,
   * og liveloggen tegner en strek i stedet — se `hentLevert`.
   *
   * Feiler den, er rapporten likevel levert. Da sier vi det, men vi sier
   * ikke at leveringen gikk galt: PDF-en ligger der redaktoeren leser den.
   */
  await supabase
    .from("log_session")
    .update({ status: "closed", closed_at: lukket })
    .eq("id", l.oektId);

  return { ok: true, versjon, filnavn: `${reportStem({ ...naming, version: versjon })}.pdf` };
}
